import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { brandsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Zap, ArrowLeft, Upload } from 'lucide-react';

const UploadProduct = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    images: [''],
    mrp: '',
    current_price: '',
    stock: '',
    sku: '',
    category: '',
    flash_sale_end: ''
  });
  const [loading, setLoading] = useState(false);

  const handleAddImage = () => {
    setFormData({ ...formData, images: [...formData.images, ''] });
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = {
        ...formData,
        images: formData.images.filter(img => img.trim() !== ''),
        mrp: parseFloat(formData.mrp),
        current_price: parseFloat(formData.current_price),
        stock: parseInt(formData.stock),
        flash_sale_end: formData.flash_sale_end || null
      };
      
      await brandsAPI.uploadProduct(data);
      toast.success('Product uploaded! Awaiting admin approval.');
      setTimeout(() => navigate('/brand/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/brand/dashboard')}
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <Upload className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-4xl font-bold">Upload Product</h1>
          </div>

          <div className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-gray-300">Product Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="description" className="text-gray-300">Description</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Product Images (URLs)</Label>
                {formData.images.map((image, index) => (
                  <Input
                    key={index}
                    data-testid={`image-input-${index}`}
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={image}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                ))}
                <Button
                  data-testid="add-image-btn"
                  type="button"
                  onClick={handleAddImage}
                  variant="outline"
                  className="mt-2 border-white/10 text-white hover:bg-white/10"
                >
                  Add Another Image
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mrp" className="text-gray-300">MRP ($)</Label>
                  <Input
                    id="mrp"
                    data-testid="mrp-input"
                    type="number"
                    step="0.01"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="current_price" className="text-gray-300">Sale Price ($)</Label>
                  <Input
                    id="current_price"
                    data-testid="price-input"
                    type="number"
                    step="0.01"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stock" className="text-gray-300">Stock Quantity</Label>
                  <Input
                    id="stock"
                    data-testid="stock-input"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="sku" className="text-gray-300">SKU</Label>
                  <Input
                    id="sku"
                    data-testid="sku-input"
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    className="mt-2 bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="category" className="text-gray-300">Category</Label>
                <Input
                  id="category"
                  data-testid="category-input"
                  type="text"
                  placeholder="e.g. Electronics, Fashion, Home"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="flash_sale_end" className="text-gray-300">Flash Sale End (Optional)</Label>
                <Input
                  id="flash_sale_end"
                  data-testid="flash-sale-input"
                  type="datetime-local"
                  value={formData.flash_sale_end}
                  onChange={(e) => setFormData({ ...formData, flash_sale_end: e.target.value })}
                  className="mt-2 bg-black/40 border-white/10 text-white"
                />
              </div>

              <Button
                data-testid="submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-lg py-6 hover:opacity-90"
              >
                {loading ? 'Uploading...' : 'Upload Product'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadProduct;
